import { useEffect, useState } from "react";
import { TimePickerMenuPosition, TimePickerSize } from "./timePicker.types";

const COMPACT_MENU_MIN_WIDTH_PX = 240;
const VIEWPORT_MARGIN_PX = 8;
const MENU_VERTICAL_OFFSET_PX = 4;
const ESTIMATED_MENU_HEIGHT_PX = 272;

export interface UseTimePickerMenuPositionProps {
  isOpen: boolean;
  size: TimePickerSize;
  triggerRef: React.RefObject<HTMLDivElement>;
}

/** Calculates and tracks the dropdown menu position relative to the trigger. */
export const useTimePickerMenuPosition = (
  props: UseTimePickerMenuPositionProps,
): TimePickerMenuPosition | null => {
  const [menuPosition, setMenuPosition] =
    useState<TimePickerMenuPosition | null>(null);

  useEffect(() => {
    if (!props.isOpen) return;
    const updatePosition = (): void => {
      const rect = props.triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const minWidth =
        props.size === "compact" ? COMPACT_MENU_MIN_WIDTH_PX : rect.width;
      const width = Math.max(rect.width, minWidth);
      const viewportWidth = window.innerWidth;
      const maxLeft = Math.max(
        VIEWPORT_MARGIN_PX,
        viewportWidth - width - VIEWPORT_MARGIN_PX,
      );
      const preferredLeft =
        props.size === "compact" ? rect.right - width : rect.left;
      const left = Math.min(
        Math.max(preferredLeft, VIEWPORT_MARGIN_PX),
        maxLeft,
      );
      const viewportHeight = window.innerHeight;
      const downTop = rect.bottom + MENU_VERTICAL_OFFSET_PX;
      const upTop =
        rect.top - ESTIMATED_MENU_HEIGHT_PX - MENU_VERTICAL_OFFSET_PX;
      const shouldOpenUpward =
        downTop + ESTIMATED_MENU_HEIGHT_PX >
        viewportHeight - VIEWPORT_MARGIN_PX;
      const unclampedTop = shouldOpenUpward ? upTop : downTop;
      const maxTop = Math.max(
        VIEWPORT_MARGIN_PX,
        viewportHeight - ESTIMATED_MENU_HEIGHT_PX - VIEWPORT_MARGIN_PX,
      );
      const top = Math.min(Math.max(unclampedTop, VIEWPORT_MARGIN_PX), maxTop);

      setMenuPosition({
        top,
        left,
        width,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [props.isOpen, props.size, props.triggerRef]);

  return menuPosition;
};
